/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to the site header
 * which appears to be displayed on almost every page
 */

// Imports
import { Link, useNavigate } from "react-router-dom";
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from "prop-types";
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash, faFolderOpen, faUser } from "@fortawesome/free-regular-svg-icons";
import { faArrowRightFromBracket, faUserLock, faUserPen } from '@fortawesome/free-solid-svg-icons'

// Interfaces
import { Dossier } from '../../interfaces/IDossiers';
import { PublicCabinet } from "../../interfaces/ICabinets";
import { MySpaceRenderableComponent } from "../../interfaces/IUtils";

// Utils
import { ApiCallConstructor, HTTPResponse } from "../utils/ApiCallConstructor";
import { ErrorForUsr, signOut } from "../utils/Errors";

// Custom components
import { FolderInput } from "../subpages/subcomponents/FolderInput";
import { PopUp } from '../buroclic/popUp';
import { isAValidPassword } from "../utils/Format";



export const Header = (props) => {
    const navigate = useNavigate();
    const cabinetAlias = localStorage.getItem('cabinetAlias')?.toLowerCase();
    const cabinet = JSON.parse(localStorage.getItem('cabinet') || "{}") as PublicCabinet;
    const [login, setLogin] = useState(localStorage.getItem('login'));
    const simulation :boolean = Boolean(localStorage.getItem('simulation')) || false;

    const showFolderInput: Boolean = Boolean(props.showFolderInput);

    // States about dossier
    const dossier = props.dossier || {} as Dossier; // Folder object

    // States about user/profile
    const fullMenuRef = useRef<HTMLDivElement>(null); // HTML Element of the user menu + icon
    const menuRef = useRef<HTMLDivElement>(null); // HTML Element of the user menu
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false); // Toggle about the user menu being open or not
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false); // Toggle about the login update pop-up
    const [isPwdModalOpen, setIsPwdModalOpen] = useState(false); // Toggle about the password update pop-up


    // At first render
    useEffect(() => {
        // Handle exiting menu with clicking everywhere else
        const handleExitingMenu = (event) => {
            // Hide the user menu
            if (menuRef.current && !menuRef.current.contains(event.target as Node) && !fullMenuRef.current?.contains(event.target as Node))
                setIsUserMenuOpen(false);
        }
        document.addEventListener('click', handleExitingMenu); // Add listener
        return () => { document.removeEventListener('click', handleExitingMenu) } // remove listener

    }, []);



    // Login management

    const [newLogin, setNewLogin] = useState(login !== null ? login : '');
    const [loginFormError, setLoginFormError] = useState('');

    useEffect(() => {
        // Since the user has edited its login, we can clear the error message
        setLoginFormError('');
    }, [newLogin]);

    
    // Handle login form submit
    const saveNewLogin = async (e) => {
        e.preventDefault();
        // Confirmation not match
        if (!/^[a-zA-Z0-9@_.-]*$/.test(newLogin)) {
            setLoginFormError('Le login ne doit pas comporter de caractères spéciaux');
            return;
        }

        // Save the new login in DB
        try {
            await toast.promise(
                ApiCallConstructor({
                    endpoint: '/account/login',
                    method: 'PUT',
                    body: { login: newLogin }
                }),
                {
                    pending: 'Sauvegarde de votre nouveau login en cours',
                    error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } },
                    success: 'Votre login a été modifié'
                });
            setIsLoginModalOpen(false); // Close modal
            setLogin(newLogin);
        } catch (err: any) {
            // Handle redirection for session expiration
            if (err.error === ErrorForUsr.NotSignedIn)
                signOut((cabAlias) => navigate(`/${cabAlias}/login`));
            
            console.error(err.error)
        }
    }



    // Password Management

    const [currentPwd, setCurrentPwd] = useState('');
    const [isCurrentPwdShown, setIsCurrentPwdShown] = useState<boolean>(false);
    const [newPwd, setNewPwd] = useState('');
    const [isNewPwdShown, setIsNewPwdShown] = useState<boolean>(false);
    const [newPwdConfirm, setNewPwdConfirm] = useState('');
    const [isNewPwdConfirmShown, setIsNewPwdConfirmShown] = useState<boolean>(false);
    const [pwdFormError, setPwdFormError] = useState('');

    // Prevent user failed while typing in new password
    useEffect(() => {
        setPwdFormError('');
    }, [currentPwd, newPwd, newPwdConfirm]);


    // Handle password edit submitting
    const saveNewPwd = async (e) => {
        e.preventDefault();

        // Empty values
        if (!currentPwd || !newPwd) {
            setPwdFormError('Veuillez renseigner un mot de passe')
            return;
        }
        
        // Not matching
        if (newPwd !== newPwdConfirm) {
            setPwdFormError('Les mots de passe ne correspondent pas');
            return;
        }

        if (!isAValidPassword(newPwd)) {
            setPwdFormError('Votre mot de passe doit contenir au minimum 8 caractères dont à minima 1 majuscule, 1 minuscule, 1 chiffre et 1 caractère spécial');
            return;
        }

        // Save the new pwd in DB
        try {
            await toast.promise(
                ApiCallConstructor({
                    endpoint: '/account/password',
                    method: 'PUT',
                    body: {
                        oldPassword: currentPwd,
                        password: newPwd
                    }
                }),
                {
                    pending: 'Sauvegarde de votre nouveau mot de passe en cours',
                    error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } },
                    success: 'Votre mot de passe a été modifié'
                });
            setIsPwdModalOpen(false); // Close modal
        } catch (err: any) {
            // Handle redirection for session expiration
            if (err.error === ErrorForUsr.NotSignedIn) 
                signOut((cabAlias) => navigate(`/${cabAlias}/login`));

            console.error(err.error)
        }
    }



    // Sign out

    // Handle signing out
    const handleLogout = async () => {
        try {
            await toast.promise(
                ApiCallConstructor({
                    endpoint: '/account/signout',
                    method: 'POST'
                }),
                {
                    pending: 'Déconnexion en cours',
                    error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } },
                    success: `A bientôt ${login} !`
                });

            // redirect to login page (simulation or standard authentication)
            if (localStorage.getItem('simulation') && !!localStorage.getItem('simulation'))
                navigate(`/${cabinetAlias}/simulation`);
            else
                navigate(`/${cabinetAlias}/login`);
        } catch (err: any) {
            // Handle redirection for session expiration
            if (err.error === ErrorForUsr.NotSignedIn)
                signOut((cabAlias) => navigate(`/${cabAlias}/login`));
        
            console.error(err.error)
            navigate(`/${cabinetAlias}/login`);
        }
    };

    
    
    // HTML Layout edits

    // Centered element
    let centeredEl;
    if (showFolderInput) {
        // Centered Element is the folder input
        centeredEl =
            <div className="w-3/6 flex items-center" style={{ margin: '0 8.333%' }}>
                <FontAwesomeIcon icon={faFolderOpen} className="w-8 h-8 lg:w-10 lg:h-10" style={{color: cabinet.color}} />
                {/*<img src='/images/folder.png' alt="Folder" className="w-8 h-8 lg:w-10 lg:h-10" />*/}
                <FolderInput key={dossier?.DOS_REF} dossier={dossier} inHeader={true} onFolderChange={props.onFolderChange} />
            </div>
    } else {
        // Centered Element is the welcoming text
        centeredEl = <div className="w-4/6">
            
        </div>;
    }


    // HTML Template
    return (
        <header className={`bg-gray-100 flex flex-row justify-center items-center w-full ${props.className || 'h-[10vh]'}`}>

            <div className="h-full w-1/6">
                {props.onLogoClick ? 
                <div onClick={() => { props.onLogoClick(MySpaceRenderableComponent.Home)}} className="h-full w-full flex" tabIndex={1}>
                    <img src={cabinet?.logo} alt="Company" className={`cursor-pointer h-full w-full object-contain object-left max-h-[90%] my-auto ml-[14px] max-w-[100%]`} />
                </div>
                :
                <Link className="h-full w-full flex" to="/accueil" state={{renderedComponent: undefined}} tabIndex={1}>
                    {/*<img src={cabinet?.logo} alt="Company" className={`${(logoInitialWidth / 2) > logoInitialHeight ? 'w-4/5 h-auto mx-auto' : 'h-4/5 w-auto ml-[14px] mr-auto'} my-auto`} />*/}
                    <img src={cabinet?.logo} alt="Company" className={`cursor-pointer h-full w-full object-contain object-left max-h-[90%] my-auto ml-[14px] max-w-[100%]`} />
                </Link>}
            </div>

            {centeredEl}


            <div ref={fullMenuRef} className="w-1/6 flex flex-col items-center">
                <FontAwesomeIcon icon={faUser} style={{color: cabinet.color}} className="w-8 h-8 cursor-pointer focus:outline-none" onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} tabIndex={showFolderInput ? 3 : 2} />
                {/*<img
                    src={'/images/profile.png'}
                    alt="Profile"
                    className="w-12 h-12 rounded-full cursor-pointer"
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    tabIndex={showFolderInput ? 3 : 2}
                />*/}

                <p className="text-center mb-0 hidden lg:block">{login}</p>

                <div ref={menuRef} className={`absolute bg-gray-100 w-48 rounded-lg shadow-md right-4 ${isUserMenuOpen ? '' : 'hidden'}`} style={{ top: '10vh', zIndex: 9999 }}>
                    <div className={`${simulation ? 'hidden' : 'flex'} items-center hover:bg-zinc-50 rounded-lg cursor-pointer`} onClick={() => setIsLoginModalOpen(!isLoginModalOpen)}>
                        <FontAwesomeIcon style={{color: cabinet.color}} className="float-right ml-2 w-5 h-5" icon={faUserPen} />
                        <button className="block text-left px-2 py-2 text-gray-700">
                            Modifier le login
                        </button>
                    </div>
                    <hr className={`${simulation ? 'hidden' : ''} m-0`} />
                    <div className={`${simulation ? 'hidden' : 'flex'} items-center hover:bg-zinc-50 rounded-lg cursor-pointer`} onClick={() => setIsPwdModalOpen(!isPwdModalOpen)}>
                        <FontAwesomeIcon style={{color: cabinet.color}} className="float-right ml-2 w-5 h-5" icon={faUserLock} />
                        <button className="block text-left px-2 py-2 text-gray-700">
                            Modifier le mot de passe
                        </button>
                    </div>
                    <hr className={`${simulation ? 'hidden' : ''} m-0`} />
                    <div className="flex items-center hover:bg-zinc-50 rounded-lg cursor-pointer" onClick={handleLogout}>
                        <FontAwesomeIcon style={{color: cabinet.color}} className="float-right ml-2 w-5 h-5" icon={faArrowRightFromBracket} />
                        <button className="block text-left px-2 py-2 text-gray-700">
                            Déconnexion
                        </button>
                    </div>
                </div>
            </div>


            {/* Pop up handling the login edit */}

            <PopUp key={`modalLogin${String(isLoginModalOpen)}`} title="Modifier mon login" className="popup-center"
                visible={isLoginModalOpen} modal
                closeHandler={() => setIsLoginModalOpen(false)}
                confirmHandler={saveNewLogin}>

                <form onSubmit={saveNewLogin}>
                    <label htmlFor="newLogin" className="text-gray-500 hidden lg:inline-block">Login:</label>
                    <input type="text" id="newLogin" autoComplete="username" value={newLogin} onChange={(e) => setNewLogin(e.target.value)}
                        className="border border-gray-300 bg-white py-2 px-2 rounded-md h-10 mb-1 lg:mb-3 shadow-bottom" style={{ width: '-webkit-fill-available' }} />

                    {loginFormError && <p className="text-red-500 mx-auto font-medium ">{loginFormError}</p>}
                    <button type="submit" className="hidden"></button>
                </form>
            </PopUp>



            {/* Pop up handling the password edit */}

            <PopUp key={`modalPwd${String(isPwdModalOpen)}`} title="Modifier mon mot de passe" className="popup-center"
                visible={isPwdModalOpen} modal
                closeHandler={() => setIsPwdModalOpen(false)}
                confirmHandler={saveNewPwd}>

                <form onSubmit={saveNewPwd}>
                    {/* Accessibility fix */}
                    <label htmlFor="currentLogin" style={{display: 'none'}}>Username</label>
                    <input readOnly type="text" style={{display: 'none'}} id="currentLogin" autoComplete="username" value={login!} />

                    <label htmlFor="currentPwd" className="text-gray-500 hidden lg:inline-block">Mot de passe actuel:</label>
                    <div className="border mb-1 lg:mb-3 flex items-center rounded-md w-full focus-within:ring-2 focus-within:ring-black">
                        <input type={`${isCurrentPwdShown ? 'text' : 'password'}`} id="currentPwd" value={currentPwd} autoComplete="current-password" onChange={(e) => setCurrentPwd(e.target.value)}
                            className="bg-white py-2 px-2 rounded-md h-10 shadow-bottom outline-none w-[inherit]" />
                        <button type='button' onClick={() => setIsCurrentPwdShown(!isCurrentPwdShown)} className="border-l border-l-gray-300 h-10 w-[4rem]">
                            <FontAwesomeIcon className={`h-7 w-7 mx-3`} icon={isCurrentPwdShown ? faEyeSlash : faEye} />
                        </button>
                    </div>

                    <label htmlFor="newPwd" className="text-gray-500 hidden lg:inline-block">Nouveau mot de passe:</label>
                    <div className="border mb-1 lg:mb-3 flex items-center rounded-md w-full focus-within:ring-2 focus-within:ring-black">
                        <input type={`${isNewPwdShown ? 'text' : 'password'}`} id="newPwd" value={newPwd} autoComplete="new-password" onChange={(e) => setNewPwd(e.target.value)}
                            className="bg-white py-2 px-2 rounded-md h-10 shadow-bottom outline-none w-[inherit]" />
                        <button type='button' onClick={() => setIsNewPwdShown(!isNewPwdShown)} className="border-l border-l-gray-300 h-10 w-[4rem]">
                            <FontAwesomeIcon className={`h-7 w-7 mx-3`} icon={isNewPwdShown ? faEyeSlash : faEye} />
                        </button>
                    </div>

                    <label htmlFor="newPwdConfirm" className="text-gray-500 hidden lg:inline-block">Confirmation du nouveau mot de passe:</label>
                    <div className="border mb-1 lg:mb-3 flex items-center rounded-md w-full focus-within:ring-2 focus-within:ring-black">
                        <input type={`${isNewPwdConfirmShown ? 'text' : 'password'}`} id="newPwdConfirm" value={newPwdConfirm} autoComplete="new-password" onChange={(e) => setNewPwdConfirm(e.target.value)}
                            className="bg-white py-2 px-2 rounded-md h-10 shadow-bottom outline-none w-[inherit]" />
                        <button type='button' onClick={() => setIsNewPwdConfirmShown(!isNewPwdConfirmShown)} className="border-l border-l-gray-300 h-10 w-[4rem]">
                            <FontAwesomeIcon className={`h-7 w-7 mx-3`} icon={isNewPwdConfirmShown ? faEyeSlash : faEye} />
                        </button>
                    </div>

                    {pwdFormError && <p className="text-red-500 mx-auto font-medium ">{pwdFormError}</p>}
                    <button type="submit" className="hidden"></button>
                </form>
            </PopUp>
        </header>
    );
};

Header.propTypes = {
    dossier: PropTypes.object,
    showFolderInput: PropTypes.bool,
    onFolderChange: PropTypes.func,
    onLogoClick: PropTypes.func,
    className: PropTypes.string
}